import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import userService from '../../../services/userService';

const ProfileEditor = ({ user, userProfile, onSuccess }) => {
  const [formData, setFormData] = useState({
    username: '',
    fullName: '',
    bio: ''
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [photoError, setPhotoError] = useState(null);

  useEffect(() => {
    if (userProfile) {
      setFormData({
        username: userProfile?.username || '',
        fullName: userProfile?.full_name || '',
        bio: userProfile?.bio || ''
      });
      setPhotoPreview(userProfile?.profile_photo_url);
    }
  }, [userProfile]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(null);
  };

  const handlePhotoUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    if (!file?.type?.startsWith('image/')) {
      setPhotoError('Please select a valid image file');
      return;
    }

    if (file?.size > 5242880) {
      setPhotoError('Image size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    setPhotoError(null);

    try {
      const { photoUrl, error: uploadError } = await userService?.uploadHeadshot(user?.id, file);

      if (uploadError) {
        throw new Error(uploadError?.message || 'Failed to upload photo');
      }

      setPhotoPreview(photoUrl);
      onSuccess?.('Profile photo updated successfully!');
    } catch (err) {
      console.error('Upload photo error:', err);
      setPhotoError(err?.message || 'Failed to upload photo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      const { error: updateError } = await userService?.updateProfile(user?.id, {
        username: formData?.username?.trim(),
        full_name: formData?.fullName?.trim(),
        bio: formData?.bio?.trim()
      });

      if (updateError) {
        throw new Error(updateError?.message || 'Failed to update profile');
      }

      onSuccess?.('Profile updated successfully!');
    } catch (err) {
      console.error('Update profile error:', err);
      setError(err?.message || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-heading font-bold text-white mb-2">
          Profile Information
        </h2>
        <p className="text-blue-100">
          Update your profile photo, name, and biography
        </p>
      </div>

      {error && (
        <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-4">
          <div className="flex items-center space-x-2">
            <Icon name="AlertTriangle" size={20} className="text-red-800" />
            <p className="text-red-800 font-medium">{error}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        {/* Profile Photo */}
        <div>
          <label className="block text-white font-medium mb-3">
            Profile Photo
          </label>
          <div className="flex items-center space-x-6">
            <div className="relative">
              {photoPreview ? (
                <img
                  src={photoPreview}
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover border-4 border-white border-opacity-20"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                  <Icon name="User" size={40} className="text-white" />
                </div>
              )}
              {isUploading && (
                <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <input
                type="file"
                id="photo-upload"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
                disabled={isUploading}
              />
              <label htmlFor="photo-upload">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => document?.getElementById('photo-upload')?.click()}
                  disabled={isUploading}
                  iconName="Upload"
                  iconPosition="left"
                  className="bg-white bg-opacity-20 text-white hover:bg-opacity-30"
                >
                  {isUploading ? 'Uploading...' : 'Upload Photo'}
                </Button>
              </label>
              {photoError && (
                <p className="text-red-300 text-sm mt-2">{photoError}</p>
              )}
              <p className="text-blue-200 text-sm mt-2">
                JPG, PNG or WebP. Max 5MB.
              </p>
            </div>
          </div>
        </div>

        {/* Username */}
        <div>
          <label className="block text-white font-medium mb-2">
            Username
          </label>
          <Input
            type="text"
            value={formData?.username}
            onChange={(e) => handleChange('username', e?.target?.value)}
            placeholder="Enter username"
            className="bg-white bg-opacity-20 text-white placeholder-blue-200 border-white border-opacity-20"
          />
        </div>

        {/* Full Name */}
        <div>
          <label className="block text-white font-medium mb-2">
            Full Name
          </label>
          <Input
            type="text"
            value={formData?.fullName}
            onChange={(e) => handleChange('fullName', e?.target?.value)}
            placeholder="Enter full name"
            className="bg-white bg-opacity-20 text-white placeholder-blue-200 border-white border-opacity-20"
          />
        </div>

        {/* Biography */}
        <div>
          <label className="block text-white font-medium mb-2">
            Biography
          </label>
          <textarea
            value={formData?.bio}
            onChange={(e) => handleChange('bio', e?.target?.value)}
            placeholder="Tell us about yourself..."
            rows={6}
            className="w-full px-4 py-3 bg-white bg-opacity-20 text-white placeholder-blue-200 border border-white border-opacity-20 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
          />
          <p className="text-blue-200 text-sm mt-2">
            {formData?.bio?.length || 0} characters
          </p>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            variant="primary"
            disabled={isSaving}
            iconName="Save"
            iconPosition="left"
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ProfileEditor;